import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  Tabs,
  Tab,
  ImageList,
  ImageListItem,
  Card,
  CardContent,
  Stack,
  IconButton,
} from "@mui/material";
import {
  DirectionsCar,
  CalendarToday,
  Speed,
  LocalGasStation,
  Palette,
  Category,
  CheckCircle,
  Pending,
  Close,
  Description,
  PictureAsPdf,
  Image,
  InsertDriveFile,
} from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import { Loader } from "../../components/CustomLoader";
import { vehicleServices } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import { IMAGE_BASE } from "../../utils/constants";

const ViewVehicle = ({ id, show, onHide }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);

  const fetchVehicleData = async () => {
    try {
      setLoading(true);
      const response = await vehicleServices.getVehicleById({ id });
      setData(response.data.data);
    } catch (error) {
      errorHandler(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id && show) {
      fetchVehicleData();
    }
  }, [id, show]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 1:
        return "success";
      case 0:
        return "error";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 1:
        return "Active";
      case 0:
        return "Inactive";
      default:
        return "Unknown";
    }
  };

  const getFileIcon = (fileName) => {
    const extension = fileName?.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return <PictureAsPdf color="error" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return <Image color="primary" />;
      case 'doc':
      case 'docx':
        return <Description color="info" />;
      default:
        return <InsertDriveFile color="action" />;
    }
  };

  const handleDownload = async (filePath) => {
    try {
      const response = await fetch(IMAGE_BASE + filePath);
      if (!response.ok) throw new Error('Failed to fetch file');

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filePath.split('/').pop(); // filename
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
      window.open(IMAGE_BASE + filePath, '_blank');
    }
  };



  const InfoCard = ({ icon, title, value, color = "primary" }) => (
    <Card
      elevation={0}
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        height: "100%",
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          boxShadow: 2,
        }
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              backgroundColor: `${color}.light`,
              color: `${color}.main`,
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", fontWeight: 600 }}>
              {title}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, mt: 0.5 }}>
              {value}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  return (
    <Dialog
      maxWidth="lg"
      fullWidth
      open={show}
      onClose={onHide}
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
          background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
          minHeight: "80vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          color: "white",
          position: "relative",
          p: 3,
        }}
        className="dialog-background"
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              {data ? `${data.year || ''} ${data.make?.name || ''} ${data.model || ''}`.trim() || 'Vehicle Details' : ''}
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400 }}>
              {data?.VIN || ''}
            </Typography>
          </Box>
          <IconButton
            onClick={onHide}
            sx={{
              color: "white",
              backgroundColor: "rgba(255,255,255,0.1)",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.2)",
              },
            }}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Loader />
        </Box>
      ) : (
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="vehicle details tabs">
              <Tab
                label="Overview"
                icon={<DirectionsCar />}
                iconPosition="start"
                sx={{ textTransform: "none", fontWeight: 600 }}
              />
              <Tab
                label="Gallery"
                icon={<Palette />}
                iconPosition="start"
                sx={{ textTransform: "none", fontWeight: 600 }}
              />
              <Tab
                label="Documents"
                icon={<Category />}
                iconPosition="start"
                sx={{ textTransform: "none", fontWeight: 600 }}
              />
            </Tabs>
          </Box>

          <Box sx={{ p: 3 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <Loader />
              </Box>
            ) : tabValue === 0 && (
              <Box>
                {/* Status Cards */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <InfoCard
                      icon={<CheckCircle />}
                      title="Status"
                      value={getStatusLabel(data?.status)}
                      color={getStatusColor(data?.status)}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <InfoCard
                      icon={<CalendarToday />}
                      title="Added"
                      value={data?.createdAt ? new Date(data.createdAt).toLocaleDateString() : "N/A"}
                      color="secondary"
                    />
                  </Grid>
                </Grid>

                {/* Vehicle Details */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {/* Vehicle Specifications - Full Width */}
                  <Card elevation={0} sx={{ border: 1, borderColor: "divider", borderRadius: 2 }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: "text.primary" }}>
                        Vehicle Specifications
                      </Typography>

                      {/* Basic Information Section */}
                      <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: "primary.main" }}>
                          Basic Information
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6} md={4}>
                            <Box sx={{ 
                              p: 2, 
                              backgroundColor: "grey.50", 
                              borderRadius: 2,
                              border: 1,
                              borderColor: "grey.200"
                            }}>
                              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                <DirectionsCar color="primary" sx={{ fontSize: 20 }} />
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                  Make
                                </Typography>
                              </Stack>
                              <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
                                {data?.make?.name || "N/A"}
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid item xs={12} sm={6} md={4}>
                            <Box sx={{ 
                              p: 2, 
                              backgroundColor: "grey.50", 
                              borderRadius: 2,
                              border: 1,
                              borderColor: "grey.200"
                            }}>
                              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                <DirectionsCar color="primary" sx={{ fontSize: 20 }} />
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                  Model
                                </Typography>
                              </Stack>
                              <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
                                {data?.model || "N/A"}
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid item xs={12} sm={6} md={4}>
                            <Box sx={{ 
                              p: 2, 
                              backgroundColor: "grey.50", 
                              borderRadius: 2,
                              border: 1,
                              borderColor: "grey.200"
                            }}>
                              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                <LocalGasStation color="primary" sx={{ fontSize: 20 }} />
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                  Fuel Type
                                </Typography>
                              </Stack>
                              <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
                                {data?.fuelType || "N/A"}
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid item xs={12} sm={6} md={4}>
                            <Box sx={{ 
                              p: 2, 
                              backgroundColor: "grey.50", 
                              borderRadius: 2,
                              border: 1,
                              borderColor: "grey.200"
                            }}>
                              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                <DirectionsCar color="primary" sx={{ fontSize: 20 }} />
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                  Transmission
                                </Typography>
                              </Stack>
                              <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
                                {data?.transmission || "N/A"}
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid item xs={12} sm={6} md={4}>
                            <Box sx={{ 
                              p: 2,
                              backgroundColor: "grey.50",
                              borderRadius: 2,
                              border: 1,
                              borderColor: "grey.200"
                            }}>
                              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                <Palette color="primary" sx={{ fontSize: 20 }} />
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                  Exterior Color
                                </Typography>
                              </Stack>
                              <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
                                {data?.exteriorColor || "N/A"}
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid item xs={12} sm={6} md={4}>
                            <Box sx={{ 
                              p: 2,
                              backgroundColor: "grey.50",
                              borderRadius: 2,
                              border: 1,
                              borderColor: "grey.200"
                            }}>
                              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                <Palette color="primary" sx={{ fontSize: 20 }} />
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                  Interior Color
                                </Typography>
                              </Stack>
                              <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
                                {data?.interiorColor || "N/A"}
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid item xs={12} sm={6} md={4}>
                            <Box sx={{ 
                              p: 2, 
                              backgroundColor: "grey.50", 
                              borderRadius: 2,
                              border: 1,
                              borderColor: "grey.200"
                            }}>
                              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                  Original Price
                                </Typography>
                              </Stack>
                              <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
                                {data?.originalPrice ? `$${data.originalPrice.toLocaleString()}` : "N/A"}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>

                      {/* Categories Section */}
                      <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: "primary.main" }}>
                          Categories
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ 
                              p: 2, 
                              backgroundColor: "primary.50", 
                              borderRadius: 2,
                              border: 1,
                              borderColor: "primary.200"
                            }}>
                              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                <Category color="primary" sx={{ fontSize: 20 }} />
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                  Categories
                                </Typography>
                              </Stack>
                              <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
                                {data?.category && data.category.length > 0 
                                  ? data.category.map(cat => cat.name || cat).join(", ")
                                  : "N/A"
                                }
                              </Typography>
                            </Box>
                          </Grid>

                          {/* Sub Category - Only show if subcategories exist */}
                          {data?.subCategory && data.subCategory.length > 0 && (
                            <Grid item xs={12} sm={6}>
                              <Box sx={{ 
                                p: 2, 
                                backgroundColor: "secondary.50", 
                                borderRadius: 2,
                                border: 1,
                                borderColor: "secondary.200"
                              }}>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                  <Category color="secondary" sx={{ fontSize: 20 }} />
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                    Sub Categories
                                  </Typography>
                                </Stack>
                                <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
                                  {data.subCategory.map(subCat => subCat.name || subCat).join(", ")}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                        </Grid>
                      </Box>

                      {/* Technical Details Section */}
                      <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: "primary.main" }}>
                          Technical Details
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6} md={4}>
                            <Box sx={{ 
                              p: 2, 
                              backgroundColor: "info.50", 
                              borderRadius: 2,
                              border: 1,
                              borderColor: "info.200"
                            }}>
                              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                <DirectionsCar color="info" sx={{ fontSize: 20 }} />
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                  Trim
                                </Typography>
                              </Stack>
                              <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
                                {data?.trim || "N/A"}
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid item xs={12} sm={6} md={4}>
                            <Box sx={{ 
                              p: 2, 
                              backgroundColor: "info.50", 
                              borderRadius: 2,
                              border: 1,
                              borderColor: "info.200"
                            }}>
                              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                <Speed color="info" sx={{ fontSize: 20 }} />
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                  Odometer
                                </Typography>
                              </Stack>
                              <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
                                {data?.miles ? `${data.miles.toLocaleString()} ${data?.odometerUnit || "KM"}` : "N/A"}
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid item xs={12} sm={6} md={4}>
                            <Box sx={{ 
                              p: 2, 
                              backgroundColor: "info.50", 
                              borderRadius: 2,
                              border: 1,
                              borderColor: "info.200"
                            }}>
                              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                <DirectionsCar color="info" sx={{ fontSize: 20 }} />
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                  Drivetrain
                                </Typography>
                              </Stack>
                              <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
                                {data?.drivetrain || "N/A"}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>

                      {data?.description && (
                        <Box sx={{ 
                          mt: 3, 
                          pt: 3, 
                          borderTop: 1, 
                          borderColor: "divider",
                          backgroundColor: "grey.50",
                          borderRadius: 2,
                          p: 3
                        }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: "primary.main" }}>
                            Description
                          </Typography>
                          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                            {data.description}
                          </Typography>
                        </Box>
                      )}
                       {data?.MVP && (
                        <Box sx={{ 
                          mt: 3, 
                          pt: 3, 
                          borderTop: 1, 
                          borderColor: "divider",
                          backgroundColor: "grey.50",
                          borderRadius: 2,
                          p: 3
                        }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: "primary.main" }}>
                            Vehicle Options
                          </Typography>
                          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                            {data.MVP}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>

                    {/* Dealer Info */}
                    <Card elevation={0} sx={{ border: 1, borderColor: "divider", borderRadius: 2 }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: "text.primary" }}>
                        Dealer Information
                      </Typography>

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={4}>
                          <Box sx={{ 
                            p: 2, 
                            backgroundColor: "grey.50", 
                            borderRadius: 2,
                            border: 1,
                            borderColor: "grey.200"
                          }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                Name
                              </Typography>
                            </Stack>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
                              {data?.dealer && typeof data.dealer === 'object' ? (data.dealer.name || 'N/A') : 'N/A'}
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                          <Box sx={{ 
                            p: 2, 
                            backgroundColor: "grey.50", 
                            borderRadius: 2,
                            border: 1,
                            borderColor: "grey.200"
                          }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                Email
                              </Typography>
                            </Stack>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
                              {data?.dealer && typeof data.dealer === 'object' ? (data.dealer.email || 'N/A') : 'N/A'}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>

                  {/* Location Information - Below Vehicle Specifications */}
                  <Card elevation={0} sx={{ border: 1, borderColor: "divider", borderRadius: 2 }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: "text.primary" }}>
                        Location Information
                      </Typography>

                      <Grid container spacing={3}>
                        {data?.location?.address && (
                          <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ 
                              p: 2, 
                              backgroundColor: "success.50", 
                              borderRadius: 2,
                              border: 1,
                              borderColor: "success.200"
                            }}>
                              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                  Address
                                </Typography>
                              </Stack>
                              <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
                                {data.location.address}
                              </Typography>
                            </Box>
                          </Grid>
                        )}

                        <Grid item xs={12} sm={6} md={3}>
                          <Box sx={{ 
                            p: 2, 
                            backgroundColor: "success.50", 
                            borderRadius: 2,
                            border: 1,
                            borderColor: "success.200"
                          }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                City
                              </Typography>
                            </Stack>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
                              {data?.location?.city || "N/A"}
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <Box sx={{ 
                            p: 2, 
                            backgroundColor: "success.50", 
                            borderRadius: 2,
                            border: 1,
                            borderColor: "success.200"
                          }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                Country
                              </Typography>
                            </Stack>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
                              {data?.location?.country || "N/A"}
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <Box sx={{ 
                            p: 2, 
                            backgroundColor: "success.50", 
                            borderRadius: 2,
                            border: 1,
                            borderColor: "success.200"
                          }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                Postal Code
                              </Typography>
                            </Stack>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
                              {data?.location?.postalCode || "N/A"}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>

                
                </Box>
              </Box>
            )}

            {!loading && tabValue === 1 && (
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: "text.primary" }}>
                  Vehicle Gallery
                </Typography>

                {data?.vehicleImage && data.vehicleImage.length > 0 ? (
                  <ImageList sx={{ width: "100%", height: 400 }} cols={3} rowHeight={200}>
                    {data.vehicleImage.map((image, index) => (
                      <ImageListItem key={index}>
                        <Box
                          component="img"
                          src={IMAGE_BASE + image}
                          alt={`${data.make?.name} ${data.model} - Image ${index + 1}`}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            borderRadius: 2,
                            boxShadow: 2,
                          }}
                        />
                      </ImageListItem>
                    ))}
                  </ImageList>
                ) : (
                  <Box
                    sx={{
                      height: 300,
                      backgroundColor: "grey.100",
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "text.secondary",
                    }}
                  >
                    <Typography variant="h6">No Images Available</Typography>
                  </Box>
                )}
              </Box>
            )}

            {tabValue === 2 && (
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: "text.primary" }}>
                  Vehicle Documents
                </Typography>

                {(() => {
                  // Check for documents using vehicleDocumentsLink first, then fallback to old structure
                  const hasDocuments = data?.vehicleDocuments && data.vehicleDocuments.length > 0 && 
                    data.vehicleDocuments.some(doc => doc.vehicleDocumentsLink || doc.image);
                  
                  if (!hasDocuments) {
                    return (
                      <Box
                        sx={{
                          height: 200,
                          backgroundColor: "grey.100",
                          borderRadius: 2,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "text.secondary",
                        }}
                      >
                        <InsertDriveFile sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                        <Typography variant="h6" sx={{ mb: 1 }}>No Documents Available</Typography>
                        <Typography variant="body2">Upload documents to view them here</Typography>
                      </Box>
                    );
                  }

                  return (
                    <Grid container spacing={2}>
                      {data.vehicleDocuments.map((doc, index) => {
                        // Use vehicleDocumentsLink first, then fallback to image
                        const documentLink = doc.vehicleDocumentsLink || doc.image;
                        if (!documentLink) return null;

                        // Get file name from link or use doc.name
                        const getFileName = () => {
                          if (doc.name) return doc.name;
                          if (documentLink) {
                            try {
                              return documentLink.split('/').pop() || 'Document';
                            } catch (e) {
                              return 'Document';
                            }
                          }
                          return 'Document';
                        };

                        // Get file extension safely
                        const getFileExtension = () => {
                          if (!documentLink) return '';
                          try {
                            const parts = documentLink.split('.');
                            return parts.length > 1 ? parts.pop()?.toUpperCase() : '';
                          } catch (e) {
                            return '';
                          }
                        };

                        const fileName = getFileName();
                        const fileExtension = getFileExtension();
                        const fullLink = documentLink.startsWith('http') ? documentLink : (IMAGE_BASE + documentLink);

                        return (
                          <Grid item xs={12} sm={6} md={4} key={doc._id || index}>
                            <Card
                              elevation={0}
                              sx={{
                                border: 1,
                                borderColor: "divider",
                                borderRadius: 2,
                                transition: "all 0.2s ease-in-out",
                                cursor: "pointer",
                                "&:hover": {
                                  boxShadow: 2,
                                  borderColor: "primary.main",
                                }
                              }}
                              onClick={() => window.open(fullLink, '_blank')}
                            >
                              <CardContent sx={{ p: 2 }}>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                  <Box
                                    sx={{
                                      p: 1,
                                      borderRadius: 2,
                                      backgroundColor: "grey.100",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    {getFileIcon(documentLink)}
                                  </Box>
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                      variant="subtitle2"
                                      sx={{
                                        fontWeight: 600,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap"
                                      }}
                                    >
                                      {fileName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {fileExtension ? `${fileExtension} Document` : 'Document'}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </CardContent>
                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                  );
                })()}
                

                {/* Condition Report Section */}
                {(() => {
                  // Use conditionReportLink first, then fallback to conditionReport
                  const conditionReportLink = data?.conditionReportLink || data?.conditionReport;
                  
                  if (!conditionReportLink) {
                    return null;
                  }

                  const getReportFileName = () => {
                    if (!conditionReportLink) return 'Condition Report';
                    try {
                      return conditionReportLink.split('/').pop() || 'Condition Report';
                    } catch (e) {
                      return 'Condition Report';
                    }
                  };

                  const fullReportLink = conditionReportLink.startsWith('http') 
                    ? conditionReportLink 
                    : (IMAGE_BASE + conditionReportLink);

                  return (
                    <Box sx={{ mt: 4 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: "text.primary" }}>
                        Condition Report
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={4}>
                          <Card
                            elevation={0}
                            sx={{
                              border: 1,
                              borderColor: "divider",
                              borderRadius: 2,
                              transition: "all 0.2s ease-in-out",
                              cursor: "pointer",
                              "&:hover": {
                                boxShadow: 2,
                                borderColor: "success.main",
                              }
                            }}
                            onClick={() => window.open(fullReportLink, '_blank')}
                          >
                            <CardContent sx={{ p: 2 }}>
                              <Stack direction="row" alignItems="center" spacing={2}>
                                <Description sx={{ color: "success.main", fontSize: 32 }} />
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    {getReportFileName()}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Condition Report
                                  </Typography>
                                </Box>
                              </Stack>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                    </Box>
                  );
                })()}
              </Box>
            )}
          </Box>


        </DialogContent>
      )}
    </Dialog>
  );
};

export default ViewVehicle;